import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown, Menu, X, Zap } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { nav as links } from "@/lib/home-content";
import { isCompanyPath, navServices } from "@/lib/offers";

function ServiceMenuItems({ items, onSelect, variant = "desktop", tone = "dark" }) {
    const light = tone === "light";
    const itemClass =
        variant === "desktop"
            ? `group flex w-full items-center px-4 py-2.5 text-left transition-colors duration-200 ${
                  light ? "hover:bg-[var(--brand-soft)]" : "hover:bg-white/5"
              }`
            : `group flex w-full items-center py-3.5 text-left border-b ${
                  light ? "border-black/5" : "border-white/5"
              }`;

    return (
        <>
            {items.map((item) => (
                <li key={item.label} role="none">
                    <button
                        type="button"
                        role="menuitem"
                        data-testid={`nav-service-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => onSelect(item)}
                        className={itemClass}
                    >
                        <span
                            className={`font-display font-semibold tracking-tight transition-colors duration-200 ${
                                light
                                    ? "text-[#0a0a0b] group-hover:text-[var(--brand)]"
                                    : "text-white group-hover:text-[#FF3B30]"
                            } ${variant === "desktop" ? "text-sm" : "text-base"}`}
                        >
                            {item.label}
                        </span>
                    </button>
                </li>
            ))}
        </>
    );
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const servicesRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const onWebsitesPage = location.pathname === "/websites";
    const isCompany = isCompanyPath(location.pathname);
    const tone = isCompany ? "light" : "dark";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setServicesOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOpen]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setServicesOpen(false);
                setMobileOpen(false);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    useEffect(() => {
        if (!servicesOpen) return undefined;

        const onClickOutside = (event) => {
            if (servicesRef.current && !servicesRef.current.contains(event.target)) {
                setServicesOpen(false);
            }
        };

        const timerId = window.setTimeout(() => {
            document.addEventListener("click", onClickOutside);
        }, 0);

        return () => {
            window.clearTimeout(timerId);
            document.removeEventListener("click", onClickOutside);
        };
    }, [servicesOpen]);

    const goHome = () => {
        setMobileOpen(false);
        if (location.pathname === "/") {
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            navigate("/");
        }
    };

    const goToSection = (href) => {
        setMobileOpen(false);
        if (href.startsWith("/")) {
            const hashIndex = href.indexOf("#");
            if (hashIndex !== -1 && location.pathname === "/") {
                scrollToId(href.slice(hashIndex));
                return;
            }
            navigate(href);
            return;
        }
        if (location.pathname === "/") {
            scrollToId(href);
        } else {
            navigate(`/${href}`);
        }
    };

    const handleServiceClick = (item) => {
        setServicesOpen(false);
        setMobileOpen(false);
        if (item.kind === "external") {
            window.location.href = item.href;
            return;
        }
        if (item.kind === "static") {
            window.location.href = item.href;
            return;
        }
        if (item.href.startsWith("/")) {
            navigate(item.href);
            return;
        }
        goToSection(item.href);
    };

    const handleCta = () => {
        setMobileOpen(false);
        if (onWebsitesPage) {
            scrollToId("#get-started");
        } else if (location.pathname === "/services/sales") {
            scrollToId("#book");
        } else {
            navigate("/services/sales#book");
        }
    };

    const ctaLabel = onWebsitesPage ? "Get My $199 Website" : "Book the diagnostic";
    const ctaShort = onWebsitesPage ? "$199 site" : "Diagnostic";

    return (
        <>
            <motion.header
                data-testid="navbar"
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className={`fixed top-0 left-0 right-0 z-[90] transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    isCompany
                        ? "px-3 pt-3 sm:px-4 sm:pt-4"
                        : `px-4 sm:px-8 lg:px-16 ${
                              scrolled
                                  ? "backdrop-blur-xl bg-[#07090E]/80 border-b border-white/10"
                                  : "bg-transparent border-b border-transparent"
                          }`
                }`}
            >
                <div
                    className={
                        isCompany
                            ? `mx-auto flex max-w-5xl items-center justify-between gap-3 rounded-2xl px-4 py-2.5 transition-all duration-300 sm:px-5 ${
                                  scrolled
                                      ? "bg-white/85 shadow-[0_10px_30px_rgba(15,23,42,0.10)] backdrop-blur-xl ring-1 ring-black/5"
                                      : "bg-white/60 backdrop-blur-md ring-1 ring-black/5"
                              }`
                            : "flex items-center justify-between py-4 max-w-[1400px] mx-auto gap-3"
                    }
                >
                    <button
                        data-testid="nav-logo"
                        onClick={goHome}
                        className="flex items-center shrink-0 gap-2"
                        aria-label="SalesCan home"
                    >
                        {isCompany ? (
                            <>
                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--brand)] text-white shadow-[0_6px_16px_rgba(43,80,255,0.35)]">
                                    <Zap className="h-4 w-4" fill="currentColor" strokeWidth={1.5} />
                                </span>
                                <span className="font-display text-lg font-extrabold tracking-tight text-[#0a0a0b]">
                                    SalesCan
                                </span>
                            </>
                        ) : (
                            <img src="/salescan-logo.png" alt="SalesCan" className="h-8 sm:h-9 w-auto" />
                        )}
                    </button>

                    <nav className="hidden lg:flex items-center gap-6">
                        {isCompany && (
                            <div
                                ref={servicesRef}
                                className="relative"
                                onMouseEnter={() => setServicesOpen(true)}
                                onMouseLeave={() => setServicesOpen(false)}
                            >
                                <button
                                    type="button"
                                    data-testid="nav-link-services"
                                    aria-expanded={servicesOpen}
                                    aria-haspopup="true"
                                    onClick={() => setServicesOpen((open) => !open)}
                                    className="inline-flex items-center gap-1 font-medium text-[#4b4b55] hover:text-[var(--brand)] transition-colors duration-300"
                                >
                                    Services
                                    <ChevronDown
                                        className={`w-3.5 h-3.5 transition-transform duration-300 ${
                                            servicesOpen ? "rotate-180 text-[var(--brand)]" : ""
                                        }`}
                                    />
                                </button>
                                <AnimatePresence>
                                    {servicesOpen && (
                                        <motion.ul
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 8 }}
                                            transition={{ duration: 0.18 }}
                                            role="menu"
                                            className="absolute left-0 top-full z-20 mt-2 w-64 overflow-hidden rounded-2xl bg-white py-2 shadow-[0_18px_44px_rgba(15,23,42,0.12)] ring-1 ring-black/5"
                                        >
                                            <ServiceMenuItems
                                                items={navServices}
                                                onSelect={handleServiceClick}
                                                variant="desktop"
                                                tone="light"
                                            />
                                        </motion.ul>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {links.map((l) => (
                            <button
                                key={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                onClick={() => goToSection(l.href)}
                                className={`transition-colors duration-300 ${
                                    isCompany
                                        ? "font-medium text-[#4b4b55] hover:text-[var(--brand)]"
                                        : "font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-white"
                                }`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <button
                            data-testid="nav-cta-button"
                            onClick={handleCta}
                            className={`group flex items-center gap-2 text-white text-xs sm:text-sm font-semibold px-3 sm:px-5 py-2.5 rounded-full transition-all duration-300 ${
                                isCompany
                                    ? "bg-[var(--brand)] shadow-[0_8px_20px_rgba(43,80,255,0.35)] hover:-translate-y-0.5"
                                    : "bg-[#FF3B30] hover:bg-[#ff5147] shadow-[0_0_24px_rgba(255,59,48,0.35)] hover:shadow-[0_0_40px_rgba(255,59,48,0.6)]"
                            }`}
                        >
                            <span className="hidden min-[420px]:inline">{ctaLabel}</span>
                            <span className="min-[420px]:hidden">{ctaShort}</span>
                            {isCompany ? (
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                            ) : (
                                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            )}
                        </button>

                        <button
                            type="button"
                            data-testid="nav-menu-button"
                            aria-label={mobileOpen ? "Close menu" : "Open menu"}
                            aria-expanded={mobileOpen}
                            onClick={() => setMobileOpen((open) => !open)}
                            className={`lg:hidden flex h-10 w-10 items-center justify-center rounded-xl transition-colors duration-300 ${
                                isCompany
                                    ? "text-[var(--brand)] hover:bg-[var(--brand-soft)]"
                                    : "rounded-full border border-white/15 text-slate-200 hover:border-white/30 hover:text-white"
                            }`}
                        >
                            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {mobileOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            onClick={() => setMobileOpen(false)}
                            className={`fixed inset-0 z-[100] backdrop-blur-sm lg:hidden ${
                                isCompany ? "bg-black/20" : "bg-[#07090E]/75"
                            }`}
                        />
                        <motion.aside
                            data-testid="nav-mobile-drawer"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                            className={`fixed top-0 right-0 bottom-0 z-[110] flex w-[88%] max-w-sm flex-col lg:hidden ${
                                isCompany
                                    ? "border-l border-black/5 bg-white"
                                    : "border-l border-white/10 bg-[#0E121B]"
                            }`}
                        >
                            <div
                                className={`flex items-center justify-between px-5 py-4 ${
                                    isCompany ? "border-b border-black/5" : "border-b border-white/10"
                                }`}
                            >
                                <span
                                    className={`font-mono-tech text-xs uppercase tracking-[0.2em] ${
                                        isCompany ? "text-[#6b6b75]" : "text-slate-400"
                                    }`}
                                >
                                    Menu
                                </span>
                                <button
                                    type="button"
                                    data-testid="nav-menu-close"
                                    aria-label="Close menu"
                                    onClick={() => setMobileOpen(false)}
                                    className={`flex h-10 w-10 items-center justify-center rounded-full ${
                                        isCompany
                                            ? "border border-black/10 text-[#0a0a0b]"
                                            : "border border-white/15 text-slate-200"
                                    }`}
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <nav className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
                                {isCompany && (
                                    <>
                                        <p className="mb-3 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-[#8a8a95]">
                                            Services
                                        </p>
                                        <ul className="mb-8">
                                            <ServiceMenuItems
                                                items={navServices}
                                                onSelect={handleServiceClick}
                                                variant="mobile"
                                                tone="light"
                                            />
                                        </ul>
                                    </>
                                )}

                                <p
                                    className={`mb-3 font-mono-tech text-[10px] uppercase tracking-[0.24em] ${
                                        isCompany ? "text-[#8a8a95]" : "text-slate-500"
                                    }`}
                                >
                                    Company
                                </p>
                                <div className="space-y-1">
                                    {links.map((l) => (
                                        <button
                                            key={l.href}
                                            type="button"
                                            data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                            onClick={() => goToSection(l.href)}
                                            className={`block w-full py-3 text-left font-mono-tech text-sm uppercase tracking-[0.18em] ${
                                                isCompany
                                                    ? "normal-case tracking-normal font-semibold text-[#0a0a0b] hover:text-[var(--brand)]"
                                                    : "text-slate-300 hover:text-white"
                                            }`}
                                        >
                                            {l.label}
                                        </button>
                                    ))}
                                </div>
                            </nav>

                            <div className={`shrink-0 p-5 ${isCompany ? "border-t border-black/5" : "border-t border-white/10"}`}>
                                <button
                                    type="button"
                                    data-testid="nav-mobile-cta-button"
                                    onClick={handleCta}
                                    className={`flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white ${
                                        isCompany
                                            ? "bg-[var(--brand)] shadow-[0_10px_24px_rgba(43,80,255,0.35)]"
                                            : "bg-[#FF3B30]"
                                    }`}
                                >
                                    {ctaLabel}
                                    <ArrowUpRight className="h-4 w-4" />
                                </button>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
